import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiShoppingCart, FiPackage, FiUsers,
  FiTruck, FiPieChart, FiSettings, FiBriefcase,
  FiChevronLeft, FiChevronRight, FiMapPin, FiActivity, FiX, FiUser
} from 'react-icons/fi';

const menuItems = [
  { path: '/', icon: <FiGrid />, label: 'Dashboard' },
  { path: '/profile', icon: <FiUser />, label: 'Profile' },
  { path: '/orders', icon: <FiShoppingCart />, label: 'Orders' },
  { path: '/products', icon: <FiPackage />, label: 'Products' },
  { path: '/inventory', icon: <FiActivity />, label: 'Inventory' },
  { path: '/branches', icon: <FiMapPin />, label: 'Branches' },
  { path: '/vendors', icon: <FiBriefcase />, label: 'Vendors' },
  { path: '/employees', icon: <FiUsers />, label: 'Employees' },
  { path: '/delivery', icon: <FiTruck />, label: 'Delivery' },
  { path: '/analytics', icon: <FiPieChart />, label: 'AI Analytics' },
  { path: '/settings', icon: <FiSettings />, label: 'Settings' },
];

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobile }) => {
  const sidebarWidth = isCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)';
  
  // On mobile, isCollapsed means "hidden"
  const mobileX = isCollapsed ? '-100%' : '0';

  return (
    <motion.div 
      initial={false}
      animate={{ 
        width: isMobile ? 'var(--sidebar-width)' : sidebarWidth,
        x: isMobile ? mobileX : 0
      }}
      transition={{ type: 'spring', damping: 20, stiffness: 150 }}
      className="sidebar vh-100 position-fixed start-0 top-0 d-flex flex-column border-end"
      style={{ 
        backgroundColor: 'var(--sidebar-bg)', 
        borderColor: 'rgba(255,255,255,0.1)',
        zIndex: 1050,
        boxShadow: isMobile && !isCollapsed ? '10px 0 30px rgba(0,0,0,0.5)' : 'none'
      }}
    >
      <div className="p-4 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <div className="rounded-2 p-1 bg-primary">
            <FiActivity className="text-white" size={20} />
          </div>
          <AnimatePresence>
            {(isMobile || !isCollapsed) && (
              <motion.h4 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fw-bold neon-text mb-0"
                style={{ fontSize: '1.2rem' }}
              >
                Franchise OS
              </motion.h4>
            )}
          </AnimatePresence>
        </div>
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="btn btn-sm text-white border-0 bg-white bg-opacity-5 rounded-circle p-2 d-flex align-items-center justify-content-center"
        >
          {isMobile ? <FiX size={18} /> : (isCollapsed ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />)}
        </button>
      </div>

      <div className="flex-grow-1 overflow-auto py-3 px-3 custom-scrollbar">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => isMobile && setIsCollapsed(true)}
            className={({ isActive }) => 
              `nav-link d-flex align-items-center gap-3 p-3 mb-2 rounded-3 transition-all ${
                isActive ? 'active-link' : 'text-inactive hover-link'
              }`
            }
          >
            <span className="fs-5 d-flex">{item.icon}</span>
            {(isMobile || !isCollapsed) && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {item.label}
              </motion.span>
            )}
          </NavLink>
        ))}
      </div>

      <div className="p-4 border-top" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        {(isMobile || !isCollapsed) && (
          <div className="glass-card p-3 small">
            <p className="mb-1 fw-bold text-white">Enterprise Plan</p>
            <div className="progress bg-dark bg-opacity-50 mb-2" style={{ height: '4px' }}>
              <div className="progress-bar bg-primary" style={{ width: '75%' }}></div>
            </div>
            <p className="mb-0 text-muted" style={{ fontSize: '10px' }}>75% Storage Used</p>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .text-inactive {
          color: #94a3b8 !important;
        }
        .active-link {
          background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
          color: white !important;
          box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
        }
        .hover-link:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white !important;
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 10px;
        }
      `}</style>
    </motion.div>
  );
};

export default Sidebar;
